import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import progressTrackerService, {
  ProgressMilestone,
  ProgressMonth,
  UpdateMilestoneRequest,
} from '../services/progressTracker.service';

const ALLOWED_EMAILS = new Set([
  'john.duplechain@gwii.com',
  'lisa.maul@gwii.com',
  'lee.marinelli@gwii.com',
  'alysse.ligon@gwii.com',
]);

type ToggleField = 'code_complete' | 'in_test' | 'completed';

const TOGGLE_COLUMNS: { field: ToggleField; label: string; activeClass: string }[] = [
  { field: 'code_complete', label: 'Code Complete', activeClass: 'bg-blue-500' },
  { field: 'in_test', label: 'In Test', activeClass: 'bg-amber-500' },
  { field: 'completed', label: 'Completed', activeClass: 'bg-emerald-500' },
];

const Toggle: React.FC<{
  checked: boolean;
  onChange: () => void;
  activeClass: string;
  disabled?: boolean;
  ariaLabel: string;
}> = ({ checked, onChange, activeClass, disabled, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    onClick={onChange}
    disabled={disabled}
    className={[
      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400',
      checked ? activeClass : 'bg-slate-300 dark:bg-slate-600',
      disabled ? 'opacity-50 cursor-not-allowed' : '',
    ].join(' ')}
  >
    <span
      aria-hidden="true"
      className={[
        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
        checked ? 'translate-x-5' : 'translate-x-0',
      ].join(' ')}
    />
  </button>
);

const ProgressTracker: React.FC = () => {
  const { accounts } = useMsal();
  const userEmail = (accounts[0]?.username || '').toLowerCase();
  const isAllowed = ALLOWED_EMAILS.has(userEmail);

  const [months, setMonths] = useState<ProgressMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await progressTrackerService.getAll();
        if (!cancelled) setMonths(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load milestones');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAllowed]);

  if (!isAllowed) {
    return <Navigate to="/home" replace />;
  }

  const totals = useMemo(() => {
    let total = 0;
    let code = 0;
    let test = 0;
    let done = 0;
    months.forEach((m) =>
      m.items.forEach((i) => {
        total += 1;
        if (i.code_complete) code += 1;
        if (i.in_test) test += 1;
        if (i.completed) done += 1;
      }),
    );
    return { total, code, test, done };
  }, [months]);

  const handleToggle = async (item: ProgressMilestone, field: ToggleField) => {
    const newValue = !item[field];
    setMonths((prev) =>
      prev.map((m) => ({
        ...m,
        items: m.items.map((i) => (i.id === item.id ? { ...i, [field]: newValue } : i)),
      })),
    );
    setSavingId(item.id);
    try {
      const payload: UpdateMilestoneRequest = { [field]: newValue };
      await progressTrackerService.update(item.id, payload);
    } catch (e) {
      // Revert on failure
      setMonths((prev) =>
        prev.map((m) => ({
          ...m,
          items: m.items.map((i) => (i.id === item.id ? { ...i, [field]: !newValue } : i)),
        })),
      );
      setError(e instanceof Error ? e.message : 'Failed to save change');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-slate-500 dark:text-slate-400">Loading progress tracker…</div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Production Workflow Progress
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Committed workflows to go live in production — toggle status for each milestone.
        </p>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <span className="text-slate-600 dark:text-slate-300">
            <span className="font-semibold">{totals.total}</span> total
          </span>
          <span className="text-blue-600 dark:text-blue-400">
            <span className="font-semibold">{totals.code}</span> code complete
          </span>
          <span className="text-amber-600 dark:text-amber-400">
            <span className="font-semibold">{totals.test}</span> in test
          </span>
          <span className="text-emerald-600 dark:text-emerald-400">
            <span className="font-semibold">{totals.done}</span> completed
          </span>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {months.map((month) => (
          <section
            key={month.month_order}
            className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <header className="border-b border-slate-200 px-5 py-3 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {month.month_name}
              </h2>
            </header>
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {month.items.map((item, idx) => {
                const isFirstOfWorkflow =
                  idx === 0 || month.items[idx - 1].workflow !== item.workflow;
                return (
                  <div key={item.id} className="px-5 py-3">
                    {isFirstOfWorkflow && (
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {item.workflow}
                      </div>
                    )}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {item.name}
                        </div>
                        {item.description && (
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {item.description}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-5">
                        {TOGGLE_COLUMNS.map((col) => (
                          <label
                            key={col.field}
                            className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
                          >
                            <Toggle
                              checked={!!item[col.field]}
                              activeClass={col.activeClass}
                              disabled={savingId === item.id}
                              onChange={() => handleToggle(item, col.field)}
                              ariaLabel={`${col.label} — ${item.name}`}
                            />
                            <span className="whitespace-nowrap">{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ProgressTracker;
